'use client';

import { ReactNode, RefObject, useEffect, useRef, useState, useTransition } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { SectionFields } from '@/types/general';
import { depseudonymizeData } from '@/actions/auth/pseudonymize';
import { pick, map } from 'lodash';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import FormFields from '@/components/FormFields';
import useTranslations from '@/hooks/useTranslations';
import Box from '@mui/material/Box';
import type { SxProps } from '@mui/material/styles';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import ReCAPTCHA from 'react-google-recaptcha';

interface DataEditorProps {
  formFields: SectionFields[];
  defaultValues?: Record<string, any>;
  onSubmit?: any;
  onCancel?: any;
  loading?: boolean | string;
  cancelText?: string;
  submitText?: string;
  submitIcon?: ReactNode;
  showSubmitButton?: boolean;
  showCancelButton?: boolean;
  formRef?: RefObject<HTMLFormElement>;
  showCard?: boolean;
  showStickyButtons?: boolean;
  isMarketingWebsite?: boolean;
  sx?: SxProps;
  recaptcha?: boolean;
  disabled?: boolean;
}

export default function DataEditor({
  formFields,
  defaultValues = {},
  onSubmit,
  onCancel,
  cancelText,
  submitText,
  submitIcon,
  showSubmitButton = true,
  showCancelButton = true,
  formRef,
  showCard = false,
  showStickyButtons = false,
  isMarketingWebsite = false,
  sx,
  recaptcha,
  disabled,
}: DataEditorProps) {
  const [isSubmitting, startTransition] = useTransition();
  const { t } = useTranslations();
  const [buttonDisabled, setButtonDisabled] = useState(recaptcha || disabled ? true : false);
  const [recaptchaCompleted, setRecaptchaCompleted] = useState(false);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {},
  });

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaCompleted(!!token);
    setButtonDisabled(false);
  };

  useEffect(() => {
    depseudonymizeData(defaultValues).then((data) => {
      const fields = pick(data, map(map(formFields, 'fields').flat(), 'field') as string[]);

      methods.reset(fields);
    });
  }, [defaultValues, formFields, methods]);

  const isSubmitDisabled =
    !methods.formState.isDirty || buttonDisabled || (recaptcha && !recaptchaCompleted);

  const handleSubmit = async (data: any) => {
    startTransition(async () => {
      if (onSubmit) {
        await onSubmit(data);
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)} ref={formRef}>
        <Box sx={sx}>
          <FormFields formFields={formFields} showCard={showCard} disabled={disabled} />
        </Box>

        {recaptcha && (
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
            onChange={handleRecaptchaChange}
            style={{
              transformOrigin: '0 0',
              marginBottom: '1rem',
            }}
          />
        )}

        <Stack
          spacing={1}
          direction={isMarketingWebsite ? 'column' : 'row'}
          sx={{
            justifyContent: 'flex-end',
            mt: showCard ? 1 : 0,
            ...(showStickyButtons && {
              position: 'sticky',
              bottom: 0,
              right: 0,
              backgroundColor: 'background.default',
              px: 1,
              py: 0.5,
              borderTop: '1px solid',
              borderColor: 'divider',
            }),
          }}
        >
          {!!onCancel && showCancelButton && (
            <Button variant='text' onClick={onCancel} color='error' disabled={isSubmitting}>
              {cancelText || t('editor.cancel')}
            </Button>
          )}
          {!!onSubmit && showSubmitButton && (
            <Button
              type='submit'
              variant={'text'}
              color='secondary'
              loading={isSubmitting}
              disabled={isSubmitDisabled}
              endIcon={submitIcon}
              sx={{ alignSelf: isMarketingWebsite ? 'flex-start' : 'flex-end' }}
            >
              {submitText || t('editor.save')}
              {isMarketingWebsite && <ArrowRightAltRoundedIcon />}
            </Button>
          )}
        </Stack>
      </form>
    </FormProvider>
  );
}
